import { removeChildren } from "@rootreeweb/js-lib";

import { render } from "./singleResult.js";
import type { IResponse, IResponseData } from "./types.js";

interface Result {
	title: string;
	body: string;
	count: string;
}
const zeroResult: Result = {
	title: `Search Results`,
	body: "<div class='no-results-found rt-search-popup-no-results'>No results found</div>",
	count: ``,
};

/**
 * Renders the search results returned from AJAX.
 *
 * @param response The response from the AJAX request.
 * @param lastQuery The last query that was searched.
 * @throws Error If the search results list element is not found.
 */
export function processResults(
	response: Required<IResponse>,
	lastQuery: string,
): void {
	if (!isLastQuery(response.data.query, lastQuery)) return;
	const resultElement = document.getElementById("searchcomplete-resultslist");
	if (!resultElement) {
		throw new Error("No search results list element found");
	}
	removeChildren(resultElement);
	const result = response.data.count
		? processContentfulResult(response.data)
		: zeroResult;
	if (result) {
		outputResults(result);
	}
}

/**
 * Checks if the query is the last query searched. Used to prevent rendering old
 * queries.
 *
 * @param query The current query.
 * @param lastQuery The last query searched.
 * @returns True if the query is the last query searched, false otherwise.
 */
function isLastQuery(query: string, lastQuery: string): boolean {
	if (!query) return true;

	const cleanedQuery = query.replace("&amp;", "&");
	return cleanedQuery === lastQuery;
}

/**
 * Processes the results from a query that returned results.
 *
 * @param response The response from the AJAX request.
 * @returns The results to display.
 */
function processContentfulResult(response: IResponseData): void | Result {
	const { results: returnedResults } = response;

	if (!returnedResults) {
		return;
	}

	let resultHtml = ``;

	returnedResults.forEach((element) => {
		resultHtml += render(element);
	});

	const results = meta(response);
	results.body = resultHtml;

	return results;
}

/**
 * Outputs the search results to the DOM.
 *
 * @param results The results to output.
 */
function outputResults(results: Result): void {
	document.querySelector("#rt-search-result-title")!.textContent =
		results.title;
	document.querySelector("#searchcomplete-resultslist")!.innerHTML =
		results.body;
	document.querySelector("#header-search-result-info")!.innerHTML =
		results.count;
}

/**
 * Creates the meta-information for the search results.
 *
 * @param response The response from the AJAX request.
 * @returns The meta-information for the search results.
 */
function meta(response: IResponseData): Result {
	if (typeof response.count === "number") {
		if (response.count > 99) {
			return {
				title: `Search Results`,
				count: `<strong>99+</strong> results for <strong>${response.query}</strong>`,
				body: ``,
			};
		} else {
			return {
				title: `Search Results`,
				count: `<strong>${response.count}</strong> results for <strong>${response.query}</strong>`,
				body: ``,
			};
		}
	} else {
		return {
			title: `Top Pages`,
			count: ``,
			body: ``,
		};
	}
}
